import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import useExperiencesSearchQuery from './useExperiencesSearchQuery'

const EXPERIENCES_SORT_BY_TYPE = 'sortBy'

export default function useExperiencesSortOptions(defaultSortBy: Experiences.SortTypes = 'type') {
  const intl = useIntl()
  const { queryParameters, replaceSearchUrl } = useExperiencesSearchQuery()

  const SORT_OPTIONS: Utils.PartialRecord<Experiences.SortTypes, { label: string }> = useMemo(() => ({
    type: {
      label: intl.formatMessage({
        id: 'U3cPaB1Z85BYuE36c75T',
        defaultMessage: 'Recommended',
        description: 'Sort option label for recommended experiences in the experiences section',
      }),
    },
    mostBooked: {
      label: intl.formatMessage({
        id: 'dtD3KGmKiFppJQrTGBYp',
        defaultMessage: 'Most booked',
        description: 'Sort option label that orders experience results by highest booking volume',
      }),
    },
    mostViewed: {
      label: intl.formatMessage({
        id: '4I6e06ayUIqWD_vXDTur',
        defaultMessage: 'Most viewed',
        description: 'Sort option label that orders experience results by highest view count',
      }),
    },
    'price.asc': {
      label: intl.formatMessage({
        id: '6h21lst9LDs-3fa686mr',
        defaultMessage: 'Price (Lowest)',
        description: 'Sort option label for lowest price experiences in the experiences section',
      }),
    },
    'price.desc': {
      label: intl.formatMessage({
        id: 'w0gkJp3XALS8Glrlsfbi',
        defaultMessage: 'Price (Highest)',
        description: 'Sort option label for highest price experiences in the experiences section',
      }),
    },
    bestValue: {
      label: intl.formatMessage({
        id: 'OlJljjzBcXLOB-hI1T8z',
        defaultMessage: 'Best Value',
        description: 'Sort option label for sorting by best value in the offer listing section',
      }),
    },
    customerRating: {
      label: intl.formatMessage({
        id: 'X3dQ1SCiQe_urahMsHNL',
        defaultMessage: 'Customer Rating (Highest)',
        description: 'Sort option label for sorting by highest customer rating',
      }),
    },
  }), [intl])
  const sortOptionFromUrl = queryParameters.get(EXPERIENCES_SORT_BY_TYPE)

  const sortBy = useMemo<Experiences.SortTypes>(() => {
    if (
      !sortOptionFromUrl ||
      !SORT_OPTIONS[sortOptionFromUrl as Experiences.SortTypes]
    ) return defaultSortBy
    return sortOptionFromUrl as Experiences.SortTypes
  }, [defaultSortBy, sortOptionFromUrl, SORT_OPTIONS])

  const setSortBy = useCallback((newSortBy: Experiences.SortTypes) => {
    const newUrlFilters = convertSortToUrl(newSortBy, queryParameters)
    replaceSearchUrl(newUrlFilters)
  }, [replaceSearchUrl, queryParameters])

  const sortOptions = SORT_OPTIONS

  return {
    sortBy,
    setSortBy,
    sortOptions,
  }
}

function convertSortToUrl(newSortBy: Experiences.SortTypes, currentSearchParams: URLSearchParams) {
  const searchParams = new URLSearchParams(currentSearchParams)
  if (newSortBy) {
    searchParams.set(EXPERIENCES_SORT_BY_TYPE, String(newSortBy))
  }
  return searchParams
}
